import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';
import {
  faArrowsRotate,
  faCircleXmark,
} from '@fortawesome/pro-regular-svg-icons';

import {
  AsyncFunction,
  KeysOf,
  PlatformApp,
  PlatformAppInstance,
} from '@typings';
import { PATH } from '@constants';
import { toast } from '@features';
import { uninstallPlatformApp } from '@services/platformApps.services';
import { getPlatformAppsInstances } from '@thunks/platformApps.thunks';
import { contextNamesSelector } from '@selectors';
import { useDispatch, useSelector } from '@hooks';
import { invariant, toastifyResponseError } from '@utils';

import { Button } from '@components/Shared';
import { BadgeStatus } from '@components/Ui';

import { PlatformAppInstanceUrls } from '../PlatformAppInstanceUrls';

type Loader = Record<'uninstalling' | 'refreshing', boolean>;

type Props = {
  state: PlatformAppInstance.State;
  createdAt: PlatformApp.Instance['createdAt'];
  instanceId: PlatformApp.Instance['id'];
  endpoints: PlatformApp.Instance['endpoints'];
  getInstance: AsyncFunction;
};
export const PlatformAppManagementHandleCenter: React.FC<Props> = ({
  state,
  createdAt,
  instanceId,
  endpoints,
  getInstance,
}) => {
  const dispatch = useDispatch();
  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);

  const navigate = useNavigate();

  const [loader, setLoader] = useImmer<Loader>({
    uninstalling: false,
    refreshing: false,
  });

  const handleLoader = useCallback(
    (name: KeysOf<Loader>, value: boolean) => {
      setLoader((state) => {
        state[name] = value;
      });
    },

    [setLoader],
  );

  const handleRefresh = async () => {
    try {
      handleLoader('refreshing', true);

      await getInstance();
    } catch (error) {
      toastifyResponseError(error);
    } finally {
      handleLoader('refreshing', false);
    }
  };

  const handleUninstall = async () => {
    try {
      handleLoader('uninstalling', true);

      invariant(projectName);
      invariant(organizationName);
      invariant(clusterName);

      const context = { organizationName, clusterName, projectName };

      await uninstallPlatformApp({ ...context, id: instanceId });
      dispatch(getPlatformAppsInstances(context));

      navigate(PATH.APPS, { replace: true });

      toast.success('App uninstalled successfully');
    } catch (error) {
      toastifyResponseError(error);
    } finally {
      handleLoader('uninstalling', false);
    }
  };

  return (
    <div className="mb-8 flex items-center justify-between">
      <BadgeStatus status={state} variant="details" createdAt={createdAt} />

      <div className="flex justify-end gap-2">
        <PlatformAppInstanceUrls urls={endpoints} />
        <Button
          variant="rebecca"
          loading={loader.refreshing}
          icon={faArrowsRotate}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
        <Button
          variant="rebecca"
          loading={loader.uninstalling}
          icon={faCircleXmark}
          className="text-error"
          onClick={handleUninstall}
        >
          Uninstall
        </Button>
      </div>
    </div>
  );
};
